/**
 * Generates a two-page clinician information brief entirely in the browser.
 *
 * This document is deliberately limited to the current public hold, source
 * boundary, and evidence requirements. It is not a pilot offer, privacy notice,
 * clinical protocol, regulatory classification, DPA, BAA, or commercial quote.
 */
import { BRAND } from "@/lib/brand";

const MARGIN = 18;
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

export async function generateClinicianBrief(): Promise<void> {
  const { default: jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const writeWrapped = (
    text: string,
    x: number,
    y: number,
    width = CONTENT_WIDTH,
    lineHeight = 5,
  ): number => {
    const lines = doc.splitTextToSize(text, width);
    doc.text(lines, x, y);
    return y + lines.length * lineHeight;
  };

  const writeBullets = (items: string[], startY: number): number => {
    let y = startY;
    for (const item of items) {
      const lines = doc.splitTextToSize(`•  ${item}`, CONTENT_WIDTH - 4);
      doc.text(lines, MARGIN + 2, y);
      y += lines.length * 5 + 1;
    }
    return y;
  };

  const heading = (title: string, y: number): number => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(25);
    doc.text(title, MARGIN, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(45);
    return y + 6;
  };

  const drawHeader = (pageNumber: number) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(125);
    doc.text(`${BRAND.nameSafe} · Clinician information`, MARGIN, 10);
    doc.text(`Page ${pageNumber} of 2`, PAGE_WIDTH - MARGIN, 10, { align: "right" });
    doc.setTextColor(25);
  };

  const drawFooter = () => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(125);
    const footer =
      "Pre-release information only. Not a pilot offer, clinical protocol, privacy notice, DPA, BAA, commercial quote, regulatory statement, or launch approval.";
    const lines = doc.splitTextToSize(footer, CONTENT_WIDTH);
    doc.text(lines, PAGE_WIDTH / 2, PAGE_HEIGHT - 13, { align: "center" });
  };

  // Page 1 — current boundary
  drawHeader(1);
  let y = 24;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(21);
  doc.setTextColor(20);
  doc.text("Clinician information", MARGIN, y);
  y += 7;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(13);
  doc.setTextColor(75);
  doc.text("Public release controls under review", MARGIN, y);
  y += 11;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(45);
  y = writeWrapped(
    "Public clinician inquiry submission, credential upload, account invitation, patient-data sharing, cohort reporting, and pilot enrollment are paused while production, privacy, security, clinical, and legal evidence is assembled and reviewed.",
    MARGIN,
    y,
  );
  y += 5;

  y = heading("Current public source boundary", y);
  y = writeBullets(
    [
      "Retrospective, self-reported wellness context only. Logged patterns may be incomplete and do not prove cause or predict what happens next.",
      "No diagnosis, treatment recommendation, clinician monitoring, emergency response, or clinical decision-support representation.",
      "No public collection of clinician contact details, credential files, license information, patient information, or health information.",
      "No public patient roster, patient-detail preview, clinical summary, data-sharing workflow, or clinician workspace demonstration.",
      "No public predictive output, wearable activation, paid clinician plan, cohort promise, response-time promise, or outcome claim.",
    ],
    y,
  );
  y += 4;

  y = heading("What can be reviewed now", y);
  y = writeBullets(
    [
      "This public information brief and the source-level release controls that keep collection and enrollment off by default.",
      "General, non-sensitive questions about the intended wellness boundary and the evidence required before a clinician program could be considered.",
      "Synthetic, non-identifiable design concepts only after the specific demonstration and claims have been reviewed and approved.",
    ],
    y,
  );
  y += 4;

  y = heading("What not to send", y);
  y = writeWrapped(
    "Do not send patient, health, medical, credential, license, insurance, treatment, diagnosis, or other sensitive information through public forms, public email links, or public phone channels. Public contact channels are limited to general business questions.",
    MARGIN,
    y,
  );
  drawFooter();

  // Page 2 — evidence required
  doc.addPage();
  drawHeader(2);
  y = 24;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.setTextColor(20);
  doc.text("Evidence required before any clinician program opens", MARGIN, y);
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(45);
  y = writeBullets(
    [
      "An inventory tied to the exact deployed revision: processors, subprocessors, hosting and processing regions, data categories, routes, logs, analytics, backups, retention, deletion, export, authentication providers, and communication providers.",
      "Authenticated synthetic-account testing of authorization, row-level isolation, invitation controls, consent and withdrawal, clinician roles, export, deletion, audit evidence, incident paths, and rollback procedures.",
      "Versioned privacy and terms notices that match actual deployed behavior, with effective date, owner, change history, and archival location.",
      "A defined eligibility and geography boundary, explicit participant and clinician responsibilities, and reviewed procedures for any sharing or export.",
      "Qualified privacy, legal, security, clinical, claims, regulatory, trademark, and accessibility review appropriate to the proposed use and jurisdictions.",
      "A written pilot or partnership scope covering owners, approvals, representative synthetic tests, support boundaries, disable procedures, acceptance criteria, and commercial terms. No public page substitutes for that agreement.",
      "Post-deployment verification tied to the deployed commit. Green source checks alone do not establish production behavior or compliance.",
    ],
    y,
  );
  y += 5;

  y = heading("Current decision rule", y);
  y = writeWrapped(
    "Until the evidence above is complete and approved, public collection, public account creation, clinician enrollment, credential upload, patient-data workflows, predictive features, wearables, paid launch, and geographic expansion remain off.",
    MARGIN,
    y,
  );
  y += 6;

  y = heading("General contact", y);
  y = writeWrapped(
    `For a general, non-sensitive business question, use the public contact information shown on ${BRAND.domain}. Do not include patient, health, medical, credential, license, or other sensitive information.`,
    MARGIN,
    y,
  );
  y += 6;

  y = heading("Document status", y);
  writeWrapped(
    "This brief describes a source-level hold and review checklist. It does not verify production controls, promise a pilot, offer services, classify the product, establish a legal relationship, or replace qualified review.",
    MARGIN,
    y,
  );

  drawFooter();
  doc.save("Myelina-clinician-information-release-controls.pdf");
}
